import { peekPendingGroup } from './groupContinue';
import { peekPendingIntro } from './introContinue';
import { peekPendingShare } from './shareContinue';

/**
 * Fields the scanner typed on Send my info, kept for the sign-up and
 * log-in pages. Scoped to the QR token in sessionStorage, same place
 * the pending share token lives, and dropped after one day.
 */

export const SCAN_SIGNUP_DRAFT = 'scan_signup_draft';
export const PENDING_INVITE_TOKEN = 'pending_invite_token';
const MAX_AGE_MS = 24 * 60 * 60 * 1000;

export const SCAN_SIGNUP_FIELDS = [
  'first_name',
  'last_name',
  'phone',
  'email',
  'company',
  'job_title',
  'city',
  'region',
  'met_where',
  'met_when',
  'met_how',
  'met_via',
  'met_city',
  'met_region',
  'note',
];

function store() {
  try {
    return window.sessionStorage;
  } catch {
    return null;
  }
}

export function scanFieldSnapshot(form) {
  const snap = {};
  for (const key of SCAN_SIGNUP_FIELDS) snap[key] = String(form?.[key] ?? '');
  return snap;
}

export function scanFieldsMatch(snapshot, form) {
  if (!snapshot) return false;
  return SCAN_SIGNUP_FIELDS.every((key) => String(snapshot[key] ?? '') === String(form?.[key] ?? ''));
}

export function stashScanSignupDraft(token, form) {
  const s = store();
  if (!s || !token) return;
  const fields = {};
  for (const key of SCAN_SIGNUP_FIELDS) {
    const value = String(form?.[key] ?? '').trim();
    if (value) fields[key] = value;
  }
  try {
    s.setItem(SCAN_SIGNUP_DRAFT, JSON.stringify({ token, savedAt: Date.now(), fields }));
  } catch {
    /* quota / private mode */
  }
}

export function peekScanSignupDraft(expectedToken) {
  const s = store();
  if (!s) return null;
  let parsed = null;
  try {
    parsed = JSON.parse(s.getItem(SCAN_SIGNUP_DRAFT) || 'null');
  } catch {
    parsed = null;
  }
  if (!parsed || typeof parsed !== 'object') return null;
  const savedAt = Number(parsed.savedAt);
  if (!savedAt || Date.now() - savedAt > MAX_AGE_MS) {
    s.removeItem(SCAN_SIGNUP_DRAFT);
    return null;
  }
  if (expectedToken && parsed.token !== expectedToken) return null;
  return parsed;
}

export function clearScanSignupDraft() {
  const s = store();
  if (s) s.removeItem(SCAN_SIGNUP_DRAFT);
}

export function stashPendingInvite(token) {
  const s = store();
  if (!s || !token) return;
  try {
    s.setItem(PENDING_INVITE_TOKEN, String(token));
  } catch {
    /* quota / private mode */
  }
}

function peekPendingInvite() {
  const s = store();
  if (!s) return '';
  return s.getItem(PENDING_INVITE_TOKEN) || '';
}

export function consumePendingInvite() {
  const token = peekPendingInvite();
  const s = store();
  if (s) s.removeItem(PENDING_INVITE_TOKEN);
  return token;
}

function pendingDraftToken() {
  return peekPendingShare()?.token
    || peekPendingGroup()?.token
    || peekPendingIntro()
    || peekPendingInvite()
    || '';
}

const PROFILE_KEYS = [
  'company', 'job_title', 'city', 'region', 'phone',
  'met_where', 'met_when', 'met_how', 'met_via', 'met_city', 'met_region',
];

/** Compact profile for the Google and Apple start URL. Empty when nothing was typed. */
export function encodeSignupProfile(fields) {
  const keep = {};
  for (const key of PROFILE_KEYS) {
    const value = String(fields?.[key] ?? '').trim();
    if (value) keep[key] = value.slice(0, 200);
  }
  if (!Object.keys(keep).length) return '';
  const bytes = new TextEncoder().encode(JSON.stringify(keep));
  let binary = '';
  bytes.forEach((b) => { binary += String.fromCharCode(b); });
  return btoa(binary).replace(/\+/g, '-').replace(/\//g, '_').replace(/=+$/g, '');
}

/** Email for the log-in page, only when this browser still has that scan. */
export function scanDraftEmail() {
  const token = pendingDraftToken();
  if (!token) return '';
  return peekScanSignupDraft(token)?.fields?.email || '';
}

/** Signup fields from the scan that sent this browser to create an account. */
export function scanDraftFields() {
  const token = pendingDraftToken();
  if (!token) return null;
  return peekScanSignupDraft(token)?.fields || null;
}
